import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { fetchNotifications, markNotificationsAsRead as apiMarkRead, markNotificationAsRead as apiMarkOneRead } from '../service/apiService';
import { Notification } from '../types/types';

interface NotificationProviderProps {
    children: React.ReactNode;
}

interface NotificationContextData {
    notifications: Notification[];
    unreadCount: number;
    loading: boolean;
    refreshNotifications: () => Promise<void>;
    markAllAsRead: () => Promise<void>;
    markAsRead: (id: number) => Promise<void>;
}

const NotificationContext = createContext<NotificationContextData>({} as NotificationContextData);

export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotifications must be used within a NotificationProvider');
    }
    return context;
};

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
    const { isAuthenticated, getAccessToken } = useAuth();
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [loading, setLoading] = useState(false);

    // Calcula contagem localmente para garantir sincronia com o estado
    const unreadCount = notifications.filter(n => !n.read).length;

    const refreshNotifications = async () => {
        const token = getAccessToken();
        if (!token || !isAuthenticated) return;

        try {
            setLoading(true);
            const data = await fetchNotifications(token);
            // Ordena por mais recente
            const sorted = data.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
            setNotifications(sorted);
        } catch (error) {
            console.error('Erro ao buscar notificações:', error);
        } finally {
            setLoading(false);
        }
    };

    const markAllAsRead = async () => {
        const token = getAccessToken();
        if (!token || unreadCount === 0) return;

        // Atualiza estado local otimista
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));

        try {
            await apiMarkRead(token);
        } catch (err) {
            console.error('Erro ao marcar notificações como lida:', err);
            // Em caso de erro, reverteria ou apenas buscaria de novo
            refreshNotifications();
        }
    };

    const markAsRead = async (id: number) => {
        const token = getAccessToken();
        const notification = notifications.find(n => n.id === id);

        if (!token || !notification || notification.read) return;

        // Atualiza estado local otimista
        setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));

        try {
            await apiMarkOneRead(id, token);
        } catch (err) {
            console.error(`Erro ao marcar notificação ${id} como lida:`, err);
            refreshNotifications();
        }
    };

    // Busca sempre que o estado de autenticação mudar para true
    useEffect(() => {
        if (isAuthenticated) {
            refreshNotifications();
        } else {
            setNotifications([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated]);

    return (
        <NotificationContext.Provider value={{
            notifications,
            unreadCount,
            loading,
            refreshNotifications,
            markAllAsRead,
            markAsRead
        }}>
            {children}
        </NotificationContext.Provider>
    );
};
